/**
 * Trust Center Admin Page
 * Tenant admins & trust_admin users configure their public trust center,
 * view/approve/reject access requests, mark policies public/private.
 */
import React, { useEffect, useState } from 'react';
import {
  Shield, Save, Plus, Trash2, Check, X, ExternalLink, Copy, Clock,
  CheckCircle2, XCircle, RefreshCcw, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/services/api';
import { SUPPORTED_FRAMEWORKS, CATEGORY_LABELS, findFramework } from '@/data/complianceFrameworks';

const TrustCenterAdmin = () => {
  const [tenant, setTenant] = useState(null);
  const [settings, setSettings] = useState(null);
  const [requests, setRequests] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [approveDialog, setApproveDialog] = useState(null); // request object
  const [grantDays, setGrantDays] = useState(15);
  const [customDays, setCustomDays] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, pRes] = await Promise.all([
        api.get('/trust-center/settings'),
        api.get('/trust-center/access-requests'),
        api.get('/grc/policies?page_size=100'),
      ]);
      setTenant(sRes.data.tenant);
      setSettings(sRes.data.settings);
      setRequests(rRes.data);
      setPolicies(pRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Trust Center');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/trust-center/settings', settings);
      toast.success('Trust Center saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const addListItem = (key, item) => setSettings((s) => ({ ...s, [key]: [...(s[key] || []), item] }));
  const removeListItem = (key, i) =>
    setSettings((s) => ({ ...s, [key]: s[key].filter((_, idx) => idx !== i) }));
  const updateListItem = (key, i, patch) =>
    setSettings((s) => ({
      ...s,
      [key]: s[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));

  const effectiveSlug = settings?.custom_slug || tenant?.slug || '';
  const pathUrl = effectiveSlug ? `${window.location.origin}/trust/${effectiveSlug}` : '';
  // Root domain (strip preview subdomain prefix if present, e.g. "amidala-checks-test.preview.emergentagent.com" -> "beskarit.com")
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const rootDomain = (() => {
    const parts = currentHost.split('.');
    // Detect *.preview.emergentagent.com pattern
    if (parts.length >= 3 && parts[parts.length - 3] === 'preview') {
      return 'beskarit.com';
    }
    // Use last 2 labels as root domain
    return parts.slice(-2).join('.');
  })();
  const subdomainUrl = effectiveSlug ? `https://trust.${effectiveSlug}.${rootDomain}` : '';
  const publicUrl = subdomainUrl || pathUrl;

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Public trust center URL copied');
  };
  const openApprove = (req) => {
    setApproveDialog(req);
    setGrantDays(req.requested_days || 15);
    setCustomDays('');
  };

  const doApprove = async () => {
    const days = grantDays === 'custom' ? parseInt(customDays, 10) : grantDays;
    if (!days || days < 1 || days > 365) {
      toast.error('Enter a valid custom duration (1-365 days)');
      return;
    }
    try {
      const res = await api.post(`/trust-center/access-requests/${approveDialog.id}/approve`, { days });
      if (res.data.requires_verification) {
        toast.success(`Access granted — verification code sent to ${approveDialog.email}`);
      } else {
        toast.success(`Access granted for ${days} day(s)`);
      }
      // Build the shareable URL — prefer subdomain URL if configured, fall back to path URL
      const shareUrl = subdomainUrl
        ? `${subdomainUrl}${res.data.access_url.replace(`/trust/${effectiveSlug}`, '')}`
        : `${window.location.origin}${res.data.access_url}`;
      navigator.clipboard.writeText(shareUrl).catch(() => {});
      setApproveDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const doReject = async (req) => {
    const reason = prompt('Reason for rejection (optional):') || '';
    try {
      await api.post(`/trust-center/access-requests/${req.id}/reject`, { reason });
      toast.success('Request rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const doRevoke = async (req) => {
    if (!window.confirm('Revoke access immediately?')) return;
    try {
      await api.post(`/trust-center/access-requests/${req.id}/revoke`);
      toast.success('Access revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const togglePolicyPublic = async (p) => {
    const next = !p.trust_center_public;
    try {
      await api.put(`/trust-center/policies/${p.id}/visibility`, { trust_center_public: next });
      toast.success(next ? 'Policy now public on Trust Center' : 'Policy set to private');
      setPolicies((prev) => prev.map((x) => (x.id === p.id ? { ...x, trust_center_public: next } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update visibility');
    }
  };

  if (loading || !settings) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
        <div className="animate-pulse h-64 bg-slate-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-6xl mx-auto" data-testid="trust-center-admin">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Shield className="w-8 h-8 text-indigo-600" />
            Trust Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Showcase your security posture publicly. Approve external access to private controls.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tenant && (
            <>
              <Button variant="outline" onClick={copyPublicUrl} data-testid="copy-public-url">
                <Copy className="w-4 h-4 mr-1.5" /> Copy URL
              </Button>
              <Button variant="outline" asChild data-testid="open-public-tc">
                <a href={pathUrl} target="_blank" rel="noreferrer">
                  Preview <ExternalLink className="w-4 h-4 ml-1.5" />
                </a>
              </Button>
            </>
          )}
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Enable + tagline card */}
      <Card className="mb-6">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <p className="font-semibold">Enable public Trust Center</p>
            <p className="text-sm text-muted-foreground">
              When enabled, your trust center becomes publicly accessible at <code className="text-xs">{pathUrl}</code>.
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setField('enabled', v)}
            data-testid="toggle-enabled"
          />
        </CardContent>
      </Card>

      <Tabs defaultValue="branding">
        <TabsList>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="controls" data-testid="tab-controls">
            Controls ({(settings.control_groups || []).length})
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">
            Policies ({policies.filter((p) => p.trust_center_public).length}/{policies.length})
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Access Requests ({requests.stats?.pending || 0})
          </TabsTrigger>
        </TabsList>

        {/* Branding */}
        <TabsContent value="branding" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Public URL</CardTitle>
              <CardDescription>
                Choose a URL slug for your trust center. Share the subdomain URL with customers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>URL slug</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">trust.</span>
                  <Input
                    value={settings.custom_slug || ''}
                    onChange={(e) => setField('custom_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder={tenant?.slug || 'your-org'}
                    className="flex-1 min-w-[160px]"
                    data-testid="custom-slug-input"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">.{rootDomain}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Lowercase letters, digits, hyphens. Leave blank to use default (<code>{tenant?.slug}</code>).
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border rounded-md p-3 bg-emerald-50/50 border-emerald-200">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-emerald-800">Production URL (recommended)</Label>
                    <button
                      onClick={() => { navigator.clipboard.writeText(subdomainUrl); toast.success('Copied'); }}
                      className="text-xs text-emerald-700 hover:underline"
                      data-testid="copy-subdomain"
                    >
                      Copy
                    </button>
                  </div>
                  <code className="text-xs text-emerald-900 break-all block" data-testid="subdomain-url">
                    {subdomainUrl}
                  </code>
                  <p className="text-[11px] text-emerald-700 mt-2">
                    Point <code>*.{rootDomain}</code> DNS wildcard to this app, or add a CNAME
                    <code> trust.{effectiveSlug}.{rootDomain}</code> → this host.
                  </p>
                </div>
                <div className="border rounded-md p-3 bg-slate-50 border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-700">Path URL (works now)</Label>
                    <button
                      onClick={() => { navigator.clipboard.writeText(pathUrl); toast.success('Copied'); }}
                      className="text-xs text-slate-700 hover:underline"
                      data-testid="copy-path-url"
                    >
                      Copy
                    </button>
                  </div>
                  <code className="text-xs text-slate-800 break-all block" data-testid="path-url">
                    {pathUrl}
                  </code>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Fallback URL, no DNS needed. Use this in dev/staging.
                  </p>
                </div>
              </div>
              <div className="flex items-start justify-between p-3 border rounded-md bg-muted/30">
                <div className="pr-4">
                  <Label>Require email verification</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Send a 6-digit code to the requester&apos;s email after approval. They must enter it before
                    accessing private controls.
                  </p>
                </div>
                <Switch
                  checked={!!settings.require_email_verification}
                  onCheckedChange={(v) => setField('require_email_verification', v)}
                  data-testid="toggle-email-verify"
                />
              </div>
            </CardContent>
          </Card>

          {/* Custom Domain (Enterprise) */}
          <CustomDomainCard settings={settings} onReload={load} />

          <Card>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tagline</Label>
                <Input value={settings.tagline || ''} onChange={(e) => setField('tagline', e.target.value)}
                       data-testid="tagline-input" />
              </div>
              <div>
                <Label>Primary color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.primary_color || '#4f46e5'}
                         onChange={(e) => setField('primary_color', e.target.value)}
                         className="w-10 h-10 rounded border" />
                  <Input value={settings.primary_color || ''} onChange={(e) => setField('primary_color', e.target.value)} />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <textarea rows={3}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          value={settings.description || ''}
                          onChange={(e) => setField('description', e.target.value)}
                          data-testid="description-input" />
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input value={settings.logo_url || ''} onChange={(e) => setField('logo_url', e.target.value)} />
              </div>
              <div>
                <Label>Contact email</Label>
                <Input value={settings.contact_email || ''} type="email"
                       onChange={(e) => setField('contact_email', e.target.value)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content: certifications, frameworks, sub-processors */}
        <TabsContent value="content" className="mt-6 space-y-6">
          <FrameworkPicker
            title="Certifications & Attestations"
            description="Pick from the supported list. Each shows a branded badge on the public page."
            selected={settings.certifications || []}
            onChange={(v) => setField('certifications', v)}
            mode="certification"
            testIdPrefix="cert"
          />

          <FrameworkPicker
            title="Compliance Frameworks"
            description="Frameworks your organization complies with. Choose a status per framework."
            selected={settings.compliance_frameworks || []}
            onChange={(v) => setField('compliance_frameworks', v)}
            mode="framework"
            testIdPrefix="framework"
          />

          <ListEditor
            title="Sub-processors"
            items={settings.sub_processors || []}
            columns={[
              { key: 'name', label: 'Name', placeholder: 'AWS' },
              { key: 'purpose', label: 'Purpose', placeholder: 'Cloud hosting' },
              { key: 'location', label: 'Location', placeholder: 'US-East' },
              { key: 'dpa_url', label: 'DPA URL', placeholder: 'https://...' },
            ]}
            onAdd={() => addListItem('sub_processors', { name: '', purpose: '', location: '', dpa_url: '' })}
            onRemove={(i) => removeListItem('sub_processors', i)}
            onUpdate={(i, patch) => updateListItem('sub_processors', i, patch)}
            testIdPrefix="subprocessor"
          />
        </TabsContent>

        {/* Controls (expandable groups) */}
        <TabsContent value="controls" className="mt-6 space-y-4">
          <ControlGroupsEditor
            groups={settings.control_groups || []}
            onChange={(v) => setField('control_groups', v)}
          />
        </TabsContent>

        {/* Policies visibility */}
        <TabsContent value="policies" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Policy visibility</CardTitle>
              <CardDescription>
                Toggle which approved policies appear publicly. Others are shown only after an access request is approved.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {policies.length === 0 && (
                <p className="text-sm text-muted-foreground py-4">No policies yet.</p>
              )}
              {policies.map((p) => (
                <div key={p.id}
                     className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                     data-testid={`policy-vis-${p.id}`}>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{p.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {p.framework && <Badge variant="outline" className="text-[10px]">{p.framework}</Badge>}
                      <Badge variant="outline" className="text-[10px]">v{p.version || 1}</Badge>
                      <Badge className={p.status === 'approved' || p.status === 'published'
                        ? 'bg-emerald-100 text-emerald-700 text-[10px]'
                        : 'bg-slate-100 text-slate-700 text-[10px]'}>
                        {p.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {p.trust_center_public ? 'Public' : 'Private'}
                    </span>
                    <Switch
                      checked={!!p.trust_center_public}
                      onCheckedChange={() => togglePolicyPublic(p)}
                      data-testid={`policy-vis-toggle-${p.id}`}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access requests */}
        <TabsContent value="requests" className="mt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-requests">
              <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <span className="text-sm text-muted-foreground">
              {requests.stats?.pending || 0} pending · {requests.stats?.approved || 0} approved ·
              {' '}{requests.stats?.rejected || 0} rejected
            </span>
          </div>

          {requests.items.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                No access requests yet. Share your Trust Center URL to receive them.
              </CardContent>
            </Card>
          )}

          {requests.items.map((r) => (
            <Card key={r.id} data-testid={`req-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{r.name}</p>
                      <Badge className={
                        r.status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                        r.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {r.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {r.email} {r.company && `· ${r.company}`}
                    </p>
                    <p className="text-sm mt-2">{r.reason}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Requested {r.requested_days} day(s) · Submitted {new Date(r.created_at).toLocaleString()}
                    </p>
                    {r.status === 'approved' && (
                      <p className="text-xs text-emerald-700 mt-1">
                        <Clock className="inline w-3 h-3 mr-1" />
                        Expires {new Date(r.expires_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'pending' && (
                      <>
                        <Button onClick={() => openApprove(r)} className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`approve-${r.id}`}>
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button variant="outline" onClick={() => doReject(r)}
                                data-testid={`reject-${r.id}`}>
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {r.status === 'approved' && (
                      <Button variant="outline" onClick={() => doRevoke(r)} className="text-red-600"
                              data-testid={`revoke-${r.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Approve dialog */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => !o && setApproveDialog(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Grant access</DialogTitle>
          </DialogHeader>
          {approveDialog && (
            <div className="space-y-4">
              <p className="text-sm">
                Granting access to <strong>{approveDialog.name}</strong> ({approveDialog.email})
              </p>
              <div>
                <Label>Duration</Label>
                <Select value={String(grantDays)} onValueChange={(v) => setGrantDays(v === 'custom' ? 'custom' : parseInt(v, 10))}>
                  <SelectTrigger data-testid="grant-days-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="15">15 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="120">120 days</SelectItem>
                    <SelectItem value="custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {grantDays === 'custom' && (
                <div>
                  <Label>Custom days (1-365)</Label>
                  <Input type="number" min="1" max="365" value={customDays}
                         onChange={(e) => setCustomDays(e.target.value)}
                         data-testid="custom-days-input" />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancel</Button>
                <Button onClick={doApprove} className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="confirm-approve">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Grant access
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ListEditor = ({ title, items, columns, onAdd, onRemove, onUpdate, testIdPrefix }) => (
  <Card>
    <CardHeader className="flex-row items-center justify-between pb-3">
      <CardTitle className="text-lg">{title}</CardTitle>
      <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${testIdPrefix}-add`}>
        <Plus className="w-4 h-4 mr-1" /> Add
      </Button>
    </CardHeader>
    <CardContent className="space-y-3">
      {items.length === 0 && <p className="text-sm text-muted-foreground">None added yet.</p>}
      {items.map((item, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start p-3 border rounded-lg"
             data-testid={`${testIdPrefix}-row-${i}`}>
          {columns.map((c) => (
            <div key={c.key}>
              <Label className="text-xs">{c.label}</Label>
              {c.type === 'select' ? (
                <Select value={item[c.key] || ''} onValueChange={(v) => onUpdate(i, { [c.key]: v })}>
                  <SelectTrigger><SelectValue placeholder={c.placeholder} /></SelectTrigger>
                  <SelectContent>
                    {c.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={item[c.key] || ''} placeholder={c.placeholder}
                       onChange={(e) => onUpdate(i, { [c.key]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="md:col-span-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => onRemove(i)} className="text-red-600"
                    data-testid={`${testIdPrefix}-remove-${i}`}>
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

/**
 * FrameworkPicker — searchable picker over SUPPORTED_FRAMEWORKS.
 * mode="certification": stores { code, name, badge_url, achieved_at, link }
 * mode="framework":     stores { code, name, badge_url, status }
 */
const FrameworkPicker = ({ title, description, selected, onChange, mode, testIdPrefix }) => {
  const [query, setQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('all');

  const selectedCodes = new Set((selected || []).map((s) => s.code));

  const filtered = SUPPORTED_FRAMEWORKS.filter((fw) => {
    if (categoryFilter !== 'all' && fw.category !== categoryFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      fw.name.toLowerCase().includes(q) ||
      fw.code.toLowerCase().includes(q) ||
      (fw.description || '').toLowerCase().includes(q)
    );
  });

  const add = (fw) => {
    if (selectedCodes.has(fw.code)) return;
    const base = { code: fw.code, name: fw.name, badge_url: fw.badge_url };
    const item = mode === 'framework'
      ? { ...base, status: 'certified' }
      : { ...base, achieved_at: '', link: '' };
    onChange([...(selected || []), item]);
  };
  const remove = (code) => onChange((selected || []).filter((s) => s.code !== code));
  const update = (code, patch) =>
    onChange((selected || []).map((s) => (s.code === code ? { ...s, ...patch } : s)));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selected chips */}
        {(selected || []).length > 0 && (
          <div className="space-y-2">
            {(selected || []).map((item) => {
              const fw = findFramework(item.code);
              return (
                <div key={item.code}
                     className="flex items-center gap-3 border rounded-lg p-3 bg-slate-50/50"
                     data-testid={`${testIdPrefix}-selected-${item.code}`}>
                  <img src={item.badge_url || fw?.badge_url} alt={item.name} className="w-10 h-10 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{item.name}</p>
                    {fw?.description && <p className="text-xs text-muted-foreground truncate">{fw.description}</p>}
                  </div>
                  {mode === 'framework' && (
                    <Select value={item.status || 'certified'} onValueChange={(v) => update(item.code, { status: v })}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid={`${testIdPrefix}-status-${item.code}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="certified">Certified</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="aligned">Aligned</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {mode === 'certification' && (
                    <>
                      <Input placeholder="Since (year)" value={item.achieved_at || ''}
                             onChange={(e) => update(item.code, { achieved_at: e.target.value })}
                             className="w-28 h-8 text-xs" />
                      <Input placeholder="Certificate URL" value={item.link || ''}
                             onChange={(e) => update(item.code, { link: e.target.value })}
                             className="w-52 h-8 text-xs" />
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => remove(item.code)} className="text-red-600"
                          data-testid={`${testIdPrefix}-remove-${item.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add picker */}
        <div className="border rounded-lg p-3 bg-white">
          <div className="flex gap-2 mb-3">
            <Input placeholder="Search frameworks (e.g. SOC 2, ISO 27001, HIPAA)..." value={query}
                   onChange={(e) => setQuery(e.target.value)}
                   data-testid={`${testIdPrefix}-search`} />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52" data-testid={`${testIdPrefix}-category`}>
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
            {filtered.map((fw) => {
              const isSelected = selectedCodes.has(fw.code);
              return (
                <button
                  key={fw.code}
                  type="button"
                  disabled={isSelected}
                  onClick={() => add(fw)}
                  data-testid={`${testIdPrefix}-option-${fw.code}`}
                  className={`flex items-center gap-2 p-2 border rounded-md text-left transition-all ${
                    isSelected
                      ? 'bg-emerald-50 border-emerald-200 cursor-not-allowed opacity-70'
                      : 'hover:border-indigo-400 hover:bg-indigo-50/50 cursor-pointer'
                  }`}
                >
                  <img src={fw.badge_url} alt={fw.code} className="w-8 h-8 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{fw.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{CATEGORY_LABELS[fw.category]}</p>
                  </div>
                  {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="col-span-full text-center text-sm text-muted-foreground py-6">
                No frameworks match your filter.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};



const ControlGroupsEditor = ({ groups, onChange }) => {
  const [expandedGroup, setExpandedGroup] = React.useState(null);

  const genId = () => Math.random().toString(36).slice(2, 10);

  const addGroup = () => {
    const g = { id: genId(), title: 'New Control Group', items: [] };
    onChange([...(groups || []), g]);
    setExpandedGroup(g.id);
  };
  const removeGroup = (idx) => {
    if (!window.confirm('Delete this control group and all items?')) return;
    onChange(groups.filter((_, i) => i !== idx));
  };
  const updateGroup = (idx, patch) => {
    onChange(groups.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };
  const addItem = (groupIdx) => {
    const item = { id: genId(), title: 'New Control', description: '', status: 'implemented', copy_link_enabled: true };
    updateGroup(groupIdx, { items: [...(groups[groupIdx].items || []), item] });
  };
  const updateItem = (groupIdx, itemIdx, patch) => {
    const items = groups[groupIdx].items.map((it, i) => (i === itemIdx ? { ...it, ...patch } : it));
    updateGroup(groupIdx, { items });
  };
  const removeItem = (groupIdx, itemIdx) => {
    updateGroup(groupIdx, { items: groups[groupIdx].items.filter((_, i) => i !== itemIdx) });
  };

  return (
    <Card data-testid="control-groups-editor">
      <CardHeader className="flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg">Controls (expandable boxes)</CardTitle>
          <CardDescription>
            Group your security controls into sections like &quot;Corporate Security&quot;, &quot;Data Protection&quot;, etc.
            Each item shows a description with a &quot;View More&quot; toggle on the public page.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={addGroup} data-testid="add-control-group">
          <Plus className="w-4 h-4 mr-1" /> Add group
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {(groups || []).length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No control groups yet. Click <strong>Add group</strong> to start.
          </p>
        )}
        {(groups || []).map((group, gi) => {
          const isOpen = expandedGroup === group.id;
          return (
            <div key={group.id || gi} className="border rounded-lg overflow-hidden" data-testid={`edit-group-${group.id}`}>
              <div className="flex items-center gap-2 p-3 bg-slate-50 border-b">
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-900"
                  onClick={() => setExpandedGroup(isOpen ? null : group.id)}
                  data-testid={`toggle-group-${group.id}`}
                >
                  {isOpen ? '▾' : '▸'}
                </button>
                <Input
                  value={group.title || ''}
                  onChange={(e) => updateGroup(gi, { title: e.target.value })}
                  placeholder="Group title (e.g. Corporate Security)"
                  className="flex-1 font-semibold"
                />
                <span className="text-xs text-muted-foreground">{(group.items || []).length} items</span>
                <Button size="sm" variant="ghost" onClick={() => removeGroup(gi)} className="text-red-600"
                        data-testid={`remove-group-${group.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {isOpen && (
                <div className="p-3 space-y-2">
                  {(group.items || []).map((item, ii) => (
                    <div key={item.id || ii} className="border rounded-md p-3 space-y-2 bg-white"
                         data-testid={`edit-item-${item.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <Input
                            value={item.title || ''}
                            onChange={(e) => updateItem(gi, ii, { title: e.target.value })}
                            placeholder="Control title (e.g. Email Protection)"
                            className="font-medium"
                          />
                          <textarea
                            rows={3}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            placeholder="Describe the control..."
                            value={item.description || ''}
                            onChange={(e) => updateItem(gi, ii, { description: e.target.value })}
                          />
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <Label className="text-xs whitespace-nowrap">Status</Label>
                              <Select value={item.status || 'implemented'}
                                      onValueChange={(v) => updateItem(gi, ii, { status: v })}>
                                <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="implemented">Implemented</SelectItem>
                                  <SelectItem value="in_progress">In progress</SelectItem>
                                  <SelectItem value="planned">Planned</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <label className="flex items-center gap-1.5 text-xs">
                              <input
                                type="checkbox"
                                checked={!!item.copy_link_enabled}
                                onChange={(e) => updateItem(gi, ii, { copy_link_enabled: e.target.checked })}
                              />
                              Show &quot;Copy Link&quot; on public page
                            </label>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(gi, ii)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => addItem(gi)}
                          data-testid={`add-item-${group.id}`}>
                    <Plus className="w-4 h-4 mr-1" /> Add item
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};



/**
 * CustomDomainCard — enterprise-tier bring-your-own-domain flow.
 * 1. Admin enters e.g. "trust.acme.com"
 * 2. Backend generates a verification TXT record
 * 3. Admin adds `_beskar-verify.trust.acme.com` TXT with the token at their DNS provider
 * 4. Admin clicks Verify → backend performs DNS lookup and marks verified
 * 5. Admin adds CNAME `trust.acme.com` → our ingress; ACME/Let's Encrypt issues TLS cert automatically
 *    (server-side ACME provisioning is a platform infra task, not implemented here)
 */
const CustomDomainCard = ({ settings, onReload }) => {
  const [domain, setDomain] = React.useState(settings.custom_domain || '');
  const [verification, setVerification] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [verifyResult, setVerifyResult] = React.useState(null);

  React.useEffect(() => {
    setDomain(settings.custom_domain || '');
  }, [settings.custom_domain]);

  const saveDomain = async () => {
    setSaving(true);
    setVerifyResult(null);
    try {
      const res = await api.post('/trust-center/custom-domain', { domain: domain.trim() });
      setVerification(res.data.verification || null);
      toast.success(res.data.message || 'Domain saved');
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.post('/trust-center/custom-domain/verify');
      setVerifyResult(res.data);
      if (res.data.verified) toast.success(res.data.message);
      else toast.error(res.data.message);
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  const isVerified = !!settings.custom_domain_verified;
  const currentToken = settings.custom_domain_verification_token || verification?.value;
  const currentHost = settings.custom_domain
    ? `_beskar-verify.${settings.custom_domain}`
    : (verification?.host || '');

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-white" data-testid="custom-domain-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-600" />
              Custom Domain
              <Badge className="bg-purple-600 text-white text-[10px] px-2 py-0.5">Enterprise</Badge>
            </CardTitle>
            <CardDescription>
              Use your own domain (e.g. <code className="text-xs">trust.acme.com</code>) instead of our subdomain.
              Requires DNS TXT verification and a CNAME record. TLS is issued automatically via Let&apos;s Encrypt.
            </CardDescription>
          </div>
          {isVerified && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
              <CheckCircle2 className="w-3 h-3" /> Verified
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label>Your domain</Label>
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.\-]/g, ''))}
              placeholder="trust.acme.com"
              data-testid="custom-domain-input"
              disabled={isVerified}
            />
          </div>
          {!isVerified && (
            <Button onClick={saveDomain} disabled={saving || !domain} data-testid="save-custom-domain">
              {saving ? 'Saving...' : 'Save & Generate Token'}
            </Button>
          )}
          {isVerified && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Remove custom domain? Your trust center will fall back to the subdomain URL.')) return;
                await api.post('/trust-center/custom-domain', { domain: '' });
                setDomain('');
                setVerification(null);
                onReload && onReload();
                toast.success('Custom domain removed');
              }}
              className="text-red-600"
              data-testid="remove-custom-domain"
            >
              Remove
            </Button>
          )}
        </div>

        {(currentToken || settings.custom_domain) && !isVerified && (
          <div className="border border-dashed border-purple-300 rounded-md p-4 bg-white space-y-3">
            <div>
              <p className="text-sm font-semibold mb-1">Step 1 — Add DNS TXT record</p>
              <p className="text-xs text-muted-foreground mb-2">
                At your DNS provider, add this record to prove ownership of <code>{settings.custom_domain}</code>.
              </p>
              <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 border rounded p-2 font-mono">
                <div>
                  <div className="text-[10px] text-slate-500 mb-0.5">Type</div>
                  <div>TXT</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-0.5">Host</div>
                  <div className="flex items-center gap-1 break-all">
                    <span>{currentHost}</span>
                    <button onClick={() => copyText(currentHost)}
                            data-testid="copy-txt-host" className="text-purple-600 shrink-0">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-0.5">Value</div>
                  <div className="flex items-center gap-1 break-all">
                    <span>{currentToken}</span>
                    <button onClick={() => copyText(currentToken)}
                            data-testid="copy-txt-value" className="text-purple-600 shrink-0">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t">
              <p className="text-xs text-muted-foreground">
                DNS changes can take up to 1 hour to propagate.
              </p>
              <Button onClick={verifyDomain} disabled={verifying} size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="verify-custom-domain">
                {verifying ? 'Checking DNS...' : 'Verify Now'}
              </Button>
            </div>
            {verifyResult && !verifyResult.verified && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                <p className="font-semibold">Not verified yet</p>
                <p>{verifyResult.message}</p>
                {verifyResult.found_records?.length > 0 && (
                  <p className="mt-1">Found: <code>{verifyResult.found_records.join(', ')}</code></p>
                )}
              </div>
            )}
          </div>
        )}

        {isVerified && (
          <div className="border border-emerald-200 rounded-md p-4 bg-emerald-50/40 space-y-2">
            <p className="text-sm font-semibold text-emerald-800">Step 2 — Point your domain to us</p>
            <p className="text-xs text-emerald-700 mb-1">
              Add a CNAME so browsers reach your trust center. TLS certificate will be issued automatically.
            </p>
            <div className="grid grid-cols-3 gap-2 text-xs bg-white border rounded p-2 font-mono">
              <div>
                <div className="text-[10px] text-slate-500 mb-0.5">Type</div>
                <div>CNAME</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 mb-0.5">Host</div>
                <div>{settings.custom_domain}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 mb-0.5">Value</div>
                <div className="flex items-center gap-1">
                  <span>trust-center.beskarit.com</span>
                  <button onClick={() => copyText('trust-center.beskarit.com')} className="text-purple-600">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-emerald-800">
              Once the CNAME resolves, your trust center will be live at{' '}
              <code className="text-emerald-900 font-semibold">https://{settings.custom_domain}</code>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



export default TrustCenterAdmin;
